import { InputLabel, Stack, Typography } from '@mui/material';
import type { ActionFunctionArgs } from '@remix-run/cloudflare';
import { json } from '@remix-run/cloudflare';
import { Form, useActionData, useSearchParams } from '@remix-run/react';
import { COLORS } from '@tbd/life-tokens';
import { PasswordInput } from '@tbd/lift-ui/components/PasswordInput';
import { TextButton } from '@tbd/lift-ui/components/TextButton';

export interface FormState {
  password: string;
  showPassword: boolean;
}

export const action = async ({ request, context }: ActionFunctionArgs) => {
  const { simcapture } = context;
  const formData = await request.formData();
  const linkId = formData.get('link-id');
  try {
    const value: string = await simcapture.post(
      '/reset-password',
      { password: formData.get('password') },
      {
        responseType: 'text',
        headers: typeof linkId === 'string' ? { 'link-id': linkId } : undefined,
      }
    );

    if (value === 'success') {
      return json({ result: value });
    }
  } catch (error) {
    console.error(error);
    return json({
      error: error instanceof Error ? error?.message : 'UNKNOWN_ERROR',
    });
  }

  // `return json(null)` (vs `return null`) is required for use{Loader,Action}Data type inference
  // see https://github.com/remix-run/remix/pull/3276#issuecomment-1155811543
  return json(null);
};

export default function ChangePassword() {
  const actionData = useActionData<typeof action>();
  const [params] = useSearchParams();
  const linkId = params.get('link') || '';
  const isSuccess =
    actionData != null &&
    typeof actionData === 'object' &&
    'result' in actionData &&
    actionData.result === 'success';

  return isSuccess ? (
    <>
      <Stack spacing={2}>
        <Stack>
          <Typography component="h1" variant="header-xs">
            Success!
          </Typography>
        </Stack>

        <Stack>
          <Typography variant="paragraph-m" component="p">
            Your password has been reset.
          </Typography>
          <Typography variant="paragraph-m" component="p">
            Click to go to the sign-in page.
          </Typography>
        </Stack>

        <Stack
          sx={{
            alignItems: 'stretch',
            marginTop: 2,
          }}
        >
          <TextButton href="/login" variant="primary" text="Go to sign in" />
        </Stack>
      </Stack>
    </>
  ) : (
    <Stack spacing={1}>
      <Stack>
        <Typography component="h1" variant="header-xs">
          Create new password
        </Typography>
      </Stack>
      <Stack>
        <Typography variant="paragraph-s" color={COLORS.neutral_600}>
          Enter a new password to regain access to your account
        </Typography>
      </Stack>

      <Form replace method="post">
        <Stack>
          <input type="hidden" name="link-id" value={linkId} />
          <InputLabel htmlFor="password">
            <Typography variant="component-regular-xxs">
              Create a new password
            </Typography>
          </InputLabel>
          <PasswordInput
            autoComplete="new-password"
            sx={{ backgroundColor: 'white' }}
          />
        </Stack>
        <Stack
          direction="column"
          sx={{
            alignItems: 'stretch',
            marginTop: 2,
          }}
        >
          <TextButton
            type="submit"
            variant="primary"
            text="Save new password"
          />
        </Stack>
      </Form>
    </Stack>
  );
}
